import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import {
	AnonymousStudentProfileSynced,
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	IAnonymousStudentService,
	StudentRepositoryStateType
} from '@studyportals/anonymous-student-interfaces';
import { StudentField } from '@studyportals/studentdomain';
import { CurrencyRateRepository } from '../infrastructure/currency-rate-repository';
import { ICurrencyRates } from '../infrastructure/i-currency-rates';
import Shared from '../../../JS/Shared';
import { CurrencyConversionServiceReadyEvent, ICurrencyConversionService } from '../interface';

/* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-type-assertion, max-len */
export class CurrencyConversionService implements ICurrencyConversionService, ISubscriber<AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced> {
	private anonymousStudent?: IAnonymousStudentService;
	private currencyRateRepository: CurrencyRateRepository = new CurrencyRateRepository();
	private failedToRetrieveCurrencyRates = false;

	constructor() {
		window.EventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, this, true);
		window.EventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, this, false);
		window.EventAggregationService.subscribeTo(AnonymousStudentProfileSynced.EventType, this, false);
	}

	public async notify(
		event: AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced
	): Promise<void> {
		if (event.eventType === AnonymousStudentServiceReady.EventType) {
			await this.handleAnonymousStudentServiceReadyEvent(event as AnonymousStudentServiceReady);
		}

		if (event.eventType === AnonymousStudentProfileUpdated.EventType) {
			await this.handleAnonymousStudentProfileUpdatedEvent(event as AnonymousStudentProfileUpdated);
		}

		if (event.eventType === AnonymousStudentProfileSynced.EventType) {
			await this.handleAnonymousStudentProfileSyncedEvent(event as AnonymousStudentProfileSynced);
		}
	}

	public async update(): Promise<void> {

		const currencyRates = await this.currencyRateRepository.loadCurrencies();

		const data = await this.anonymousStudent!.getStudentData([StudentField.CURRENCY]);

		if (data[StudentField.CURRENCY] !== undefined) {
			this.failedToRetrieveCurrencyRates = false;
			this.convertCurrencies(data[StudentField.CURRENCY], currencyRates);
			this.updateCurrencyText(data[StudentField.CURRENCY]);
			this.updateBasedOnOriginalPrice();
		}
	}

	public async convert(
		originalAmount: number,
		originalCurrency: string,
		targetCurrency: string | null = null
	): Promise<number> {
		const currencyRates = await this.currencyRateRepository.loadCurrencies();
		const data = await this.anonymousStudent!.getStudentData([StudentField.CURRENCY]);

		const contentCurrency = targetCurrency || data[StudentField.CURRENCY] || null;
		if (!contentCurrency) {
			return originalAmount;
		}

		const exchangeRate = this.getExchangeRate(originalCurrency, contentCurrency, currencyRates);
		if (!exchangeRate) {
			return originalAmount;
		}

		return originalAmount * exchangeRate;
	}

	private async handleAnonymousStudentServiceReadyEvent(event: AnonymousStudentServiceReady): Promise<void> {
		this.anonymousStudent = event.anonymousStudentService;
		await this.update();
		this.fireReadyEvent();
	}

	private async handleAnonymousStudentProfileUpdatedEvent(event: AnonymousStudentProfileUpdated): Promise<void> {
		const changes = event.changes;
		if (changes[StudentField.CURRENCY]) {
			await this.update();
		}
	}

	private async handleAnonymousStudentProfileSyncedEvent(event: AnonymousStudentProfileSynced): Promise<void> {
		if (event.state === StudentRepositoryStateType.ONLINE) {
			await this.update();
		}
	}

	private convertCurrencies(userCurrency: string, currencyRates: ICurrencyRates): void {
		const currencyElements = document.querySelectorAll('[data-currency-rewrite]') as NodeListOf<HTMLElement>;

		for (const currencyElement of currencyElements) {
			const contentCurrency = currencyElement.dataset.currency;
			const amount = currencyElement.dataset.amount;
			const decimal = Number(currencyElement.dataset.decimal) || undefined;
			const currencySymbol = currencyElement.dataset.currencySymbol;

			// Check mandatory settings
			if (!contentCurrency || !amount || !currencySymbol) {
				continue;
			}

			let exchangeRate = 1.0;

			try {
				exchangeRate = this.getExchangeRate(contentCurrency, userCurrency, currencyRates);
				this.updateCurrencyElement(
					currencyElement,
					userCurrency,
					Number(amount),
					exchangeRate,
					currencySymbol,
					decimal
				);
			} catch (e) {
				this.logError(e);
				this.fallbackToDefaultCurrency(currencyElement);
			}
		}
	}

	private updateCurrencyText(currency: string): void {
		let activeCurrency = currency;
		const dataCurrencyTextElements = document.querySelectorAll('[data-currency-text]') as NodeListOf<HTMLElement>;

		if (this.failedToRetrieveCurrencyRates) {
			activeCurrency = '';
		}

		for (const dataCurrencyTextElement of dataCurrencyTextElements) {
			dataCurrencyTextElement.dataset.currencyText = activeCurrency;
			dataCurrencyTextElement.innerHTML = activeCurrency;
		}
	}

	private updateCurrencyElement(
		currencyElement: HTMLElement,
		userCurrency: string,
		amount: number,
		exchangeRate: number,
		currencySymbol: string,
		decimal = 0
	): void {
		if (!currencyElement.hasAttribute('data-original_html')) {
			currencyElement.dataset.original_html = currencyElement.innerHTML;
		}

		const newAmount = amount * exchangeRate;
		const showDecimals = amount / newAmount >= 1000 && newAmount <= 10;
		decimal = showDecimals ? 2 : decimal;

		// Safari does not support toLocaleString, so we need a backup symbol
		const symbol = userCurrency;

		const newHtml = Shared.formatAsLocalisedCurrency(newAmount, userCurrency, symbol, decimal, currencySymbol);
		const originalHtml = currencyElement.dataset.original_html;

		currencyElement.dataset.converted_amount = newAmount.toString(10);
		currencyElement.innerHTML = newHtml;
		currencyElement.title = `Converted from ${originalHtml!.replace('&nbsp;', ' ')}`;
	}

	private getExchangeRate(contentCurrency: string, userCurrency: string, currencyRates: ICurrencyRates): number {
		try {

			if (contentCurrency === userCurrency) {
				return 1;
			}

			let rateToSource = 1;
			let rateFromSourceToUser = 1;

			if (contentCurrency !== currencyRates.source) {
				rateToSource = this.getExchangeRateBetweenSourceAndTarget(currencyRates, contentCurrency);
			}

			if (userCurrency !== currencyRates.source) {
				rateFromSourceToUser = this.getExchangeRateBetweenSourceAndTarget(currencyRates, userCurrency);
			}

			return (1 / rateToSource) * rateFromSourceToUser;
		} catch (e) {
			this.failedToRetrieveCurrencyRates = true;
			throw e;
		}
	}

	private getExchangeRateBetweenSourceAndTarget(currencyRates: ICurrencyRates, currency: string): number {
		const currencyRate = currencyRates.rates[currency];

		if (!currencyRate) {
			throw new Error(`No rate for ${currencyRates.source} to ${currency}`);
		}

		return currencyRate.rate;
	}

	private fallbackToDefaultCurrency(currencyElement: HTMLElement): void {
		if (currencyElement.hasAttribute('data-original_html')) {
			currencyElement.innerHTML = currencyElement.dataset.original_html!;
		}
		currencyElement.removeAttribute('title');
	}

	private fireReadyEvent(): void {
		const event = new CurrencyConversionServiceReadyEvent(this);
		window.EventAggregationService.publishTo(CurrencyConversionServiceReadyEvent.EventType, event);
	}

	private updateBasedOnOriginalPrice(): void {
		const basedOnElements = document.getElementsByClassName('js-basedOn') as HTMLCollectionOf<HTMLElement>;

		for (const basedOnElement of basedOnElements) {
			const originalAmount = basedOnElement.dataset.originalAmount;
			const currencyAmountElement = basedOnElement.parentElement!.querySelector(
				'.js-currencyAmount'
			) as HTMLElement;

			basedOnElement.classList.remove('Hidden');
			if (currencyAmountElement) {
				if (currencyAmountElement.dataset.converted_amount === originalAmount) {
					basedOnElement.classList.add('Hidden');
				}
			} else {
				// Price is free!
				basedOnElement.classList.add('Hidden');
			}
		}
	}

	private logError(e: any): void {
		console.error(e);
	}
}
